import { useState, useRef } from 'react'
import { Sparkles, Wand2, Copy, Check, Info } from 'lucide-react'

export default function EditorSandbox() {
    const [text, setText] = useState('Type or paste your text here to see InkSpot in action...')
    const [analyzing, setAnalyzing] = useState(false)
    const [suggestions, setSuggestions] = useState([])
    const [copied, setCopied] = useState(false)
    const editorRef = useRef(null)

    const handleAnalyze = () => {
        setAnalyzing(true)
        // Mock analysis
        setTimeout(() => {
            setSuggestions([
                { id: 1, type: 'cliché', text: 'in action', replacement: 'working', explanation: 'Overused phrase.' },
                { id: 2, type: 'weak', text: 'see', replacement: 'observe', explanation: 'Weak verb.' }
            ])
            setAnalyzing(false)
        }, 1500)
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="bg-slate-900 rounded-2xl border border-white/10 overflow-hidden shadow-2xl max-w-4xl mx-auto my-12 animate-fade-in-up">
            {/* Toolbar */}
            <div className="bg-slate-800/50 p-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="flex gap-1.5 px-3">
                        <div className="w-3 h-3 rounded-full bg-red-500/50" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                        <div className="w-3 h-3 rounded-full bg-green-500/50" />
                    </div>
                    <span className="text-xs font-medium text-slate-400">InkSpot Sandbox</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleCopy}
                        className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
                        title="Copy text"
                    >
                        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* Editor Area */}
            <div className="grid grid-cols-1 md:grid-cols-3 min-h-[400px]">
                {/* Input */}
                <div className="md:col-span-2 p-6 border-r border-white/5 bg-slate-950/50">
                    <textarea
                        ref={editorRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="w-full h-full bg-transparent border-none focus:ring-0 text-slate-300 resize-none font-mono leading-relaxed placeholder-slate-600"
                        placeholder="Start typing..."
                        spellCheck="false"
                    />
                </div>

                {/* Sidebar / Analysis */}
                <div className="bg-slate-900/50 p-4 border-t md:border-t-0 md:border-l border-white/5 flex flex-col">
                    <div className="mb-4">
                        <button
                            onClick={handleAnalyze}
                            disabled={analyzing || !text.trim()}
                            className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {analyzing ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Analyzing...
                                </>
                            ) : (
                                <>
                                    <Wand2 className="w-4 h-4" />
                                    Fix My Text
                                </>
                            )}
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar">
                        {suggestions.length > 0 ? (
                            suggestions.map((s) => (
                                <div key={s.id} className="p-3 rounded-lg bg-slate-800 border border-white/5 animate-slide-in">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${s.type === 'cliché' ? 'bg-orange-500/20 text-orange-400' : 'bg-yellow-500/20 text-yellow-400'
                                            }`}>
                                            {s.type}
                                        </span>
                                        <button
                                            onClick={() => {
                                                setText(text.replace(s.text, s.replacement))
                                                setSuggestions(suggestions.filter(sug => sug.id !== s.id))
                                            }}
                                            className="text-xs text-blue-400 hover:text-blue-300"
                                        >
                                            Apply
                                        </button>
                                    </div>
                                    <div className="text-sm text-slate-300 mb-1">
                                        <span className="line-through opacity-50">{s.text}</span>
                                        <span className="mx-2">→</span>
                                        <span className="text-green-400 font-medium">{s.replacement}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 flex items-center gap-1">
                                        <Info className="w-3 h-3" /> {s.explanation}
                                    </p>
                                </div>
                            ))
                        ) : (
                            !analyzing && (
                                <div className="text-center text-slate-500 mt-10">
                                    <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                    <p className="text-sm">Hit "Fix My Text" to see AI suggestions.</p>
                                </div>
                            )
                        )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/5 text-center">
                        <p className="text-xs text-slate-500">
                            Sign up to get unlimited checks & more patterns.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
